import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
    Loader2,
    Plus,
    Trash2,
    Edit3,
    Users,
    UserCog,
    X,
    AlertCircle,
    Mail,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AdminShell from "@/components/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SENIORITY_RANK = ["lead", "senior", "mid", "support"];
const TIERS = ["starter", "studio", "sprint"];

const SENIORITY_BADGE = {
    lead: "border-ember/60 text-ember",
    senior: "border-violet-400/40 text-violet-400",
    mid: "border-sky-400/40 text-sky-400",
    support: "border-white/15 text-bone-500",
};

export default function AdminTeam() {
    const { user, token, loading } = useAuth();
    const [busy, setBusy] = useState(true);
    const [team, setTeam] = useState([]);
    const [editing, setEditing] = useState(null);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const refresh = () => {
        setBusy(true);
        return axios
            .get(`${API}/admin/team`, { headers })
            .then((r) => setTeam(r.data || []))
            .catch(() => toast.error("Could not load team"))
            .finally(() => setBusy(false));
    };

    useEffect(() => {
        if (token) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!user) return <Navigate to="/admin/login" replace />;

    const remove = async (id) => {
        if (!window.confirm("Remove this team member? Existing client assignments will be unassigned but historical snapshots remain.")) return;
        try {
            await axios.delete(`${API}/admin/team/${id}`, { headers });
            toast.success("Team member removed");
            refresh();
        } catch {
            toast.error("Could not remove");
        }
    };

    const resendInvite = async (id) => {
        try {
            await axios.post(`${API}/admin/team/${id}/resend-invite`, {}, { headers });
            toast.success("Invite email sent");
        } catch {
            toast.error("Could not resend invite");
        }
    };

    const totalCapacity = team.reduce((s, m) => s + (m.active ? m.max_clients : 0), 0);
    const totalLoad = team.reduce((s, m) => s + (m.client_count || 0), 0);
    const leadCount = team.filter((m) => m.seniority === "lead" && m.active).length;

    return (
        <AdminShell>
            <div className="max-w-6xl">
                <div className="flex items-center gap-2 mb-3">
                    <UserCog className="w-4 h-4 text-ember" />
                    <p className="text-xs uppercase tracking-[0.25em] text-ember">
                        Briefcase · Team
                    </p>
                </div>
                <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
                    <h1 className="font-display text-bone text-5xl tracking-tightest leading-[0.95]">
                        Team & managers.
                    </h1>
                    <button
                        onClick={() => setEditing({})}
                        data-testid="new-team-member"
                        className="btn-ember inline-flex items-center gap-2 h-11 px-5 text-xs uppercase tracking-[0.2em]"
                    >
                        <Plus className="w-4 h-4" />
                        Add team member
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-10">
                    <Stat label="Active members" value={busy ? "—" : team.filter((m) => m.active).length} />
                    <Stat
                        label="Workload"
                        value={
                            busy ? "—" : `${totalLoad} / ${totalCapacity}`
                        }
                        sub={
                            totalCapacity > 0
                                ? `${Math.round((totalLoad / totalCapacity) * 100)}% utilized`
                                : "Set capacity per member"
                        }
                    />
                    <Stat
                        label="Leads available"
                        value={busy ? "—" : leadCount}
                        accent={leadCount === 0 && team.length > 0}
                    />
                </div>

                {busy ? (
                    <div className="text-bone-500 text-sm flex items-center gap-3">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading…
                    </div>
                ) : team.length === 0 ? (
                    <div className="border border-dashed border-white/10 p-16 text-center">
                        <Users className="w-8 h-8 text-bone-500 mx-auto mb-4" />
                        <p className="text-bone text-lg mb-2">
                            No team members yet.
                        </p>
                        <p className="text-bone-500 text-sm mb-6 max-w-md mx-auto">
                            Add yourself + your managers and assign which tiers
                            (Starter / Studio / Sprint) they handle. New clients
                            can then be auto-assigned by tier and current
                            workload.
                        </p>
                        <button
                            onClick={() => setEditing({})}
                            className="btn-ember inline-flex items-center gap-2 h-11 px-5 text-sm tracking-wide"
                        >
                            Add your first team member
                        </button>
                    </div>
                ) : (
                    <ul data-testid="team-list" className="space-y-3">
                        {team.map((m) => (
                            <TeamCard
                                key={m.id}
                                member={m}
                                onEdit={() => setEditing(m)}
                                onRemove={() => remove(m.id)}
                                onResend={() => resendInvite(m.id)}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {editing && (
                <TeamEditModal
                    member={editing}
                    headers={headers}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        refresh();
                    }}
                />
            )}
        </AdminShell>
    );
}

function TeamCard({ member, onEdit, onRemove, onResend }) {
    const utilization = member.max_clients
        ? Math.round(((member.client_count || 0) / member.max_clients) * 100)
        : 0;
    const isOver = utilization >= 100;

    return (
        <li
            data-testid={`team-${member.id}`}
            className={`border bg-ink-800/40 p-5 transition-all ${member.active ? "border-white/10 hover:border-ember/40" : "border-white/5 opacity-60"}`}
        >
            <div className="flex items-start gap-5 flex-wrap">
                {/* Avatar */}
                <div className="w-14 h-14 shrink-0 border border-ember/40 bg-ember/5 flex items-center justify-center overflow-hidden">
                    {member.avatar_url ? (
                        <img
                            src={member.avatar_url}
                            alt={member.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <span className="font-display text-ember text-xl tracking-tight">
                            {(member.name || "?")
                                .split(" ")
                                .map((s) => s[0])
                                .slice(0, 2)
                                .join("")
                                .toUpperCase()}
                        </span>
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-display text-bone text-xl tracking-tight">
                            {member.name}
                        </h3>
                        <span
                            className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${SENIORITY_BADGE[member.seniority] || ""}`}
                        >
                            {member.seniority}
                        </span>
                        {!member.active && (
                            <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border border-white/15 text-bone-500">
                                inactive
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-bone-300">
                        {member.role}
                    </p>
                    <a
                        href={`mailto:${member.email}`}
                        className="text-xs text-bone-500 hover:text-ember transition-colors"
                    >
                        {member.email}
                    </a>

                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {(member.tier_pool || []).map((t) => (
                            <span
                                key={t}
                                className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-white/15 text-bone-300"
                            >
                                {t}
                            </span>
                        ))}
                        {(member.tier_pool || []).length === 0 && (
                            <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                No tier assigned — won't be auto-picked
                            </span>
                        )}
                    </div>
                </div>

                {/* Workload bar */}
                <div className="w-full sm:w-48 shrink-0">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] mb-1.5">
                        <span className="text-bone-500">Load</span>
                        <span
                            className={
                                isOver
                                    ? "text-red-400"
                                    : utilization > 80
                                      ? "text-amber-400"
                                      : "text-bone-300"
                            }
                        >
                            {member.client_count || 0} / {member.max_clients}
                            {isOver && (
                                <AlertCircle className="w-3 h-3 inline ml-1" />
                            )}
                        </span>
                    </div>
                    <div className="h-1 bg-white/5 overflow-hidden">
                        <div
                            data-testid={`load-${member.id}`}
                            className={`h-full transition-all duration-700 ${
                                isOver
                                    ? "bg-red-400"
                                    : utilization > 80
                                      ? "bg-amber-400"
                                      : "bg-ember"
                            }`}
                            style={{
                                width: `${Math.min(utilization, 100)}%`,
                            }}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={onResend}
                        data-testid={`resend-invite-${member.id}`}
                        title={member.has_password ? "Send password reset invite" : "Send portal setup invite"}
                        className="btn-outline-bone inline-flex items-center gap-1 h-9 px-3 text-xs uppercase tracking-[0.18em]"
                    >
                        <Mail className="w-3 h-3" />
                        {member.has_password ? "Re-invite" : "Send invite"}
                    </button>
                    <button
                        onClick={onEdit}
                        data-testid={`edit-team-${member.id}`}
                        className="btn-outline-bone inline-flex items-center gap-1 h-9 px-3 text-xs uppercase tracking-[0.18em]"
                    >
                        <Edit3 className="w-3 h-3" />
                        Edit
                    </button>
                    <button
                        onClick={onRemove}
                        data-testid={`remove-team-${member.id}`}
                        className="text-bone-500 hover:text-red-400 h-9 px-3 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-1"
                    >
                        <Trash2 className="w-3 h-3" />
                    </button>
                </div>
            </div>
        </li>
    );
}

function TeamEditModal({ member, headers, onClose, onSaved }) {
    const isNew = !member?.id;
    const [form, setForm] = useState(() => ({
        name: member?.name || "",
        role: member?.role || "",
        email: member?.email || "",
        avatar_url: member?.avatar_url || "",
        tier_pool: member?.tier_pool || [],
        seniority: member?.seniority || "mid",
        max_clients: member?.max_clients || 10,
        active: member?.active !== false,
        bio: member?.bio || "",
    }));
    const [busy, setBusy] = useState(false);

    const set = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.value }));
    const toggleTier = (t) =>
        setForm((s) => ({
            ...s,
            tier_pool: s.tier_pool.includes(t)
                ? s.tier_pool.filter((x) => x !== t)
                : [...s.tier_pool, t],
        }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.role.trim() || !form.email.trim()) {
            toast.error("Name, role and email are required");
            return;
        }
        setBusy(true);
        const payload = {
            ...form,
            max_clients: parseInt(form.max_clients, 10) || 10,
            avatar_url: form.avatar_url || null,
            bio: form.bio || null,
        };
        try {
            if (isNew) {
                await axios.post(`${API}/admin/team`, payload, { headers });
                toast.success("Team member added");
            } else {
                await axios.patch(
                    `${API}/admin/team/${member.id}`,
                    payload,
                    { headers },
                );
                toast.success("Team member updated");
            }
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-ink border border-white/15 max-w-2xl w-full p-7 lg:p-10 my-8">
                <div className="flex items-start justify-between mb-6 gap-4">
                    <h3 className="font-display text-bone text-3xl tracking-tightest">
                        {isNew ? "Add team member" : "Edit team member"}
                    </h3>
                    <button
                        onClick={onClose}
                        data-testid="team-modal-close"
                        className="text-bone-500 hover:text-bone"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form
                    onSubmit={onSubmit}
                    data-testid="team-form"
                    className="space-y-5"
                >
                    <Input label="Name *" value={form.name} onChange={set("name")} testid="team-name" />
                    <Input label="Role / title *" value={form.role} onChange={set("role")} testid="team-role" placeholder="Account Manager · Creative Director · …" />
                    <Input label="Email *" type="email" value={form.email} onChange={set("email")} testid="team-email" />
                    <Input label="Avatar URL" value={form.avatar_url} onChange={set("avatar_url")} testid="team-avatar" placeholder="https://… (square image)" />

                    <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                            Tier pool — which plans this member handles
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {TIERS.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => toggleTier(t)}
                                    data-testid={`team-tier-${t}`}
                                    className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                        form.tier_pool.includes(t)
                                            ? "border-ember text-ember bg-ember/10"
                                            : "border-white/15 text-bone-300 hover:border-white/30"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Select
                            label="Seniority"
                            value={form.seniority}
                            onChange={set("seniority")}
                            testid="team-seniority"
                            options={SENIORITY_RANK}
                        />
                        <Input
                            label="Max clients"
                            type="number"
                            value={form.max_clients}
                            onChange={set("max_clients")}
                            testid="team-max"
                        />
                    </div>

                    <Textarea
                        label="Bio (shown on client portal)"
                        value={form.bio}
                        onChange={set("bio")}
                        testid="team-bio"
                        rows={3}
                    />

                    <label className="inline-flex items-center gap-2 text-sm text-bone-300">
                        <input
                            type="checkbox"
                            checked={form.active}
                            onChange={(e) =>
                                setForm((s) => ({ ...s, active: e.target.checked }))
                            }
                            data-testid="team-active"
                            className="w-4 h-4 accent-ember"
                        />
                        Active — included in auto-assign rotation
                    </label>

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="team-submit"
                        className="btn-ember w-full inline-flex items-center justify-center gap-2 h-12 text-sm tracking-wide disabled:opacity-60"
                    >
                        {busy ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>{isNew ? "Add member" : "Save changes"}</>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Stat({ label, value, sub, accent }) {
    return (
        <div
            className={`p-5 border ${accent ? "border-amber-400/50 bg-amber-400/5" : "border-white/10 bg-ink-800/40"}`}
        >
            <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </p>
            <p
                className={`font-display tracking-tight text-2xl ${accent ? "text-amber-400" : "text-bone"}`}
            >
                {value}
            </p>
            {sub && (
                <p className="text-[10px] uppercase tracking-[0.2em] text-bone-500 mt-1">
                    {sub}
                </p>
            )}
        </div>
    );
}

function Input({ label, value, onChange, testid, type = "text", placeholder }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-2.5 text-bone placeholder:text-bone-500 text-sm transition-colors"
            />
        </label>
    );
}

function Textarea({ label, value, onChange, testid, rows = 3 }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <textarea
                value={value}
                onChange={onChange}
                rows={rows}
                data-testid={testid}
                className="w-full bg-transparent border border-white/10 focus:border-ember outline-none p-3 text-bone placeholder:text-bone-500 text-sm transition-colors resize-none"
            />
        </label>
    );
}

function Select({ label, value, onChange, testid, options }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <select
                value={value}
                onChange={onChange}
                data-testid={testid}
                className="w-full bg-ink border border-white/15 focus:border-ember outline-none px-3 h-10 text-bone text-xs uppercase tracking-[0.18em]"
            >
                {options.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
        </label>
    );
}
